import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import PerText from '../../components/custom_components/Text'
import { useCompaniesAndCountriesOfCompanyManager } from '../../graphql/company/hooks/useCompaniesAndCountriesOfCompanyManager'
import { FeatureNameEnum } from '../../graphql/featureToggle/FeatureNameEnum'
import useIsFeatureEnabled from '../../graphql/featureToggle/useIsFeatureEnabled'
import { CultivatingTalentsPageContent } from './CultivatingTalentsPageContent'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'
import { CultivatingTalentsViewSelector } from './CultivatingTalentsViewSelector'
import { RankingViewContentWrapper } from './RankingViewContentWrapper'
import { useCollapse } from '../../routes/collapse-context'
import { useAuth } from '../../context/auth-context'
import { RoleName } from '../../types'

export const CultivatingTalentsPage: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [initStore, storeInitialized, resetStore] = useCultivatingTalentsStore(
    (state) => [state.initStore, state.storeInitialized, state.resetStore],
    shallow,
  )

  const { isFeatureEnabled } = useIsFeatureEnabled(FeatureNameEnum.TALENTS_SEASON)
  const { data, error, loading } = useCompaniesAndCountriesOfCompanyManager(true)
  const collapsed = useCollapse()

  useEffect(() => {
    if (!!data && data.findCompaniesAndCountriesByCompanyManager && data.findCompaniesAndCountriesByCompanyManager.length) {
      initStore(data.findCompaniesAndCountriesByCompanyManager)
    }
    return () => {
      resetStore()
    }
  }, [data, initStore, resetStore])

  const hasPermissionToView = (): boolean => {
    return user.roles && (user.roles.includes(RoleName.CompanyManager) || user.roles.includes(RoleName.BusinessPartner)) ? true : false
  }

  if (
    !hasPermissionToView() ||
    loading ||
    error ||
    !data ||
    !data.findCompaniesAndCountriesByCompanyManager ||
    (!!data.findCompaniesAndCountriesByCompanyManager.length && !storeInitialized)
  ) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors
          isLoading={loading}
          hasNoData={!data || !data.findCompaniesAndCountriesByCompanyManager}
          isPermissionDenied={!error && !hasPermissionToView()}
          error={error}
        />
      </ContentContainer>
    )
  } else if (data.findCompaniesAndCountriesByCompanyManager.length === 0) {
    return (
      <PerInfoBox>
        <InfoBoxTile
          heading={t('rankingView.empty.heading')}
          headingAlign="center"
          contentAlign="center"
          headingColor="secondary"
          headingMt="xl"
        >
          <PerText>{t('rankingView.empty.message')}</PerText>
        </InfoBoxTile>
      </PerInfoBox>
    )
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <CultivatingTalentsViewSelector />
      {isFeatureEnabled ? <CultivatingTalentsPageContent /> : <RankingViewContentWrapper />}
    </ContentContainer>
  )
}
